/**
 * Domain detection utility for routing between builder and viewer apps
 *
 * Local development:
 *   - Builder: http://localhost:3000
 *   - Viewer: http://localhost:3000?subdomain=acme-limo
 *
 * Production:
 *   - Builder: https://app.bookedrides.com (set via VITE_BUILDER_DOMAIN)
 *   - Viewer: https://acme-limo.bookedridespages.com (set via VITE_VIEWER_DOMAIN)
 */

export type DomainType = 'builder' | 'viewer' | 'custom'

export interface DomainInfo {
  type: DomainType
  subdomain: string | null
}

/**
 * Detects whether the current domain is the builder app or a viewer site
 * @returns Domain information including type and subdomain (if applicable)
 */
export function getDomainInfo(): DomainInfo {
  const hostname = window.location.hostname
  const builderDomain = import.meta.env.VITE_BUILDER_DOMAIN
  const viewerDomain = import.meta.env.VITE_VIEWER_DOMAIN

  // Local development - check for subdomain query parameter or sessionStorage
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    const params = new URLSearchParams(window.location.search)
    const subdomain = params.get('subdomain')

    // If subdomain in URL, store it in sessionStorage for persistence during navigation
    if (subdomain) {
      sessionStorage.setItem('viewer_subdomain', subdomain)
      return { type: 'viewer', subdomain }
    }

    // Check sessionStorage for previously set subdomain (for navigation within viewer)
    const storedSubdomain = sessionStorage.getItem('viewer_subdomain')
    if (storedSubdomain) {
      return { type: 'viewer', subdomain: storedSubdomain }
    }

    return { type: 'builder', subdomain: null }
  }

  // Local development with subdomain (e.g., test-limo.localhost:3001)
  // This is the viewer app running on port 3001
  if (hostname.endsWith('.localhost')) {
    const subdomain = hostname.replace('.localhost', '')
    return { type: 'viewer', subdomain }
  }

  // Production builder app (exact match or Vercel preview/production domains)
  if (hostname === builderDomain) {
    return { type: 'builder', subdomain: null }
  }

  // Vercel deployment domains - treat as builder
  if (hostname.endsWith('.vercel.app')) {
    return { type: 'builder', subdomain: null }
  }

  // Production viewer site (subdomain of viewer domain)
  if (hostname.endsWith(`.${viewerDomain}`)) {
    const subdomain = hostname.replace(`.${viewerDomain}`, '')
    return { type: 'viewer', subdomain }
  }

  // Direct viewer domain without subdomain (redirect or error case)
  if (hostname === viewerDomain) {
    return { type: 'viewer', subdomain: null }
  }

  // Custom domain (future feature) - treat as viewer with hostname as identifier
  return { type: 'custom', subdomain: hostname }
}

/**
 * Checks if the current domain is the builder app
 */
export function isBuilderDomain(): boolean {
  return getDomainInfo().type === 'builder'
}

/**
 * Checks if the current domain is a viewer site
 */
export function isViewerDomain(): boolean {
  const { type } = getDomainInfo()
  return type === 'viewer' || type === 'custom'
}

/**
 * Gets the subdomain for the current viewer site
 * Returns null if on builder domain
 */
export function getViewerSubdomain(): string | null {
  return getDomainInfo().subdomain
}

/**
 * Builds the URL customers actually visit for an account: the custom booking
 * domain (e.g. https://book.azlimo.com) when one is connected, else the
 * platform subdomain. Prefer this over getPublicSiteUrl (below) for anything
 * shown to or pasted by an operator — getPublicSiteUrl ignores custom_domain
 * and would point tenants on custom domains at the wrong host.
 */
export function getCustomerSiteUrl(account: {
  slug: string
  custom_domain?: string | null
}): string {
  if (account.custom_domain) {
    return `https://${account.custom_domain}`
  }
  return getPublicSiteUrl(account.slug)
}

/**
 * Builds the public site URL for a given account slug
 */
export function getPublicSiteUrl(slug: string): string {
  const viewerDomain = import.meta.env.VITE_VIEWER_DOMAIN

  // Local development - point to viewer app on port 3001
  if (
    window.location.hostname === 'localhost' ||
    window.location.hostname === '127.0.0.1'
  ) {
    return `http://${slug}.localhost:3001`
  }

  // Production
  return `https://${slug}.${viewerDomain}`
}
